import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task, type TaskInstance } from 'ember-concurrency';
import type { TotalRevenuePortfolio } from 'lumen/models';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';

export default class TotalRevenueMyhotelsService extends Service {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  @tracked _myHotelsTaskInstance?: TaskInstance<TotalRevenuePortfolio[] | null>;
  @tracked marketId?: string;

  requestMyHotelsTask = task(
    { restartable: true },
    async (marketId: string) => {
      try {
        const result = await this.store.query<TotalRevenuePortfolio>(
          'total-revenue/portfolio',
          {
            namespace: APIGATEWAY,
            typeKey: 'totalrevenue/myhotels',
            market_id: marketId,
          },
        );

        return [...result];
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        return null;
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get myHotelsTaskInstance() {
    const marketId = this.hotelAndMarketService.market.id;
    const isDifferentMarket = this.marketId !== marketId;

    if (isDifferentMarket || !this._myHotelsTaskInstance) {
      // @ts-expect-error: Type 'string | null' is not assignable to type 'string | undefined'.
      // eslint-disable-next-line ember/no-side-effects
      this.marketId = marketId;

      // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
      // eslint-disable-next-line ember/no-side-effects
      this._myHotelsTaskInstance = this.requestMyHotelsTask.perform(marketId);
    }

    return this._myHotelsTaskInstance;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get portfolios() {
    return this.myHotelsTaskInstance?.value ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get properties() {
    return this.portfolios.flatMap((portfolio) => [...portfolio.hotels]);
  }
}

declare module '@ember/service' {
  interface Registry {
    'total-revenue/myhotels': TotalRevenueMyhotelsService;
  }
}
